import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import * as ExecutionsService from '../services/ExecutionsService';
import {Execution} from '../types';
import {notifyError, notifySuccess} from '../../../components/PlatformNotification';

// Query keys
export const executionsKeys = {
    all: ['executions'] as const,
    lists: () => [...executionsKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...executionsKeys.lists(), {filters}] as const,
    details: () => [...executionsKeys.all, 'detail'] as const,
    detail: (id: string) => [...executionsKeys.details(), id] as const,
};

// Hooks
export const useExecutionsQuery = (filters: Record<string, string> = {}) => {
    return useQuery({
        queryKey: executionsKeys.list(filters),
        queryFn: () => ExecutionsService.fetchRecords(filters),
    });
};

export const useExecutionQuery = (id: string) => {
    return useQuery({
        queryKey: executionsKeys.detail(id),
        queryFn: () => ExecutionsService.fetch(id),
        enabled: !!id,
    });
};

export const useInvalidateExecutions = () => {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({queryKey: executionsKeys.all});
};

export const useStartExecutionMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ExecutionsService.start,
        onSuccess: (execution) => {
            notifySuccess('Execution started');
            queryClient.invalidateQueries({queryKey: executionsKeys.lists()});
            if (execution?.id) {
                queryClient.setQueryData(executionsKeys.detail(execution.id), execution);
            }
        },
        onError: notifyError,
    });
};

export const useCancelExecutionMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ExecutionsService.cancel,
        onSuccess: (execution) => {
            notifySuccess('Execution cancellation requested');
            queryClient.invalidateQueries({queryKey: executionsKeys.lists()});
            if (execution?.id) {
                queryClient.setQueryData<Execution | undefined>(
                    executionsKeys.detail(execution.id),
                    (current) => {
                        if (!current) return execution;
                        return {...current, ...execution, phases: current.phases};
                    },
                );
            }
        },
        onError: notifyError,
    });
};
